import * as React from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
} from "@mui/material";

import {
  Menu as MenuIcon,
  Info as InfoIcon,
  MiscellaneousServices as MiscellaneousServicesIcon,
  Contacts as ContactsIcon,
  EventNote as EventNoteIcon,
} from "@mui/icons-material";
import TopHeader from "../topHeader/TopHeader";
import "./header.css";
import { useNavigate } from 'react-router-dom';
import CustomButton from "../generic-components/Button/Button";

// Define navigation data with routes, names, and icons for consistent navigation
const navigationItems = [
  { name: "About Us", route: "/about-us", icon: <InfoIcon /> },
  { name: "Services", route: "/services", icon: <MiscellaneousServicesIcon /> },
  { name: "Contact", route: "/contact-us", icon: <ContactsIcon /> },
  { name: "Careers", route: "/careers", icon: <ContactsIcon /> }
];

// Company logo component for reuse
const CompanyLogo = React.memo(({ onClick, className, iconClassName, typographyClassName }) => (
  <Box className={className} onClick={onClick} sx={{ cursor: "pointer", display: 'flex', alignItems: 'center' }}>
    <img 
    src={process.env.PUBLIC_URL + '/assets/aspire-logo.png'} alt="Aspire Logo" 
    style={{ height: 30, marginRight: 8 }} 
    />
  </Box>
));

// Navigation buttons component extracted for reuse and memoization
const NavButtons = React.memo(({ navigate }) => (
  <>
    {navigationItems.map((item) => (
      <Button
        disableRipple={true}
        key={item.name}
        onClick={() => navigate(item.route)}
        className="header__nav-button"
      >
        {item.name}
      </Button>
    ))}
    <CustomButton
      startIcon={<EventNoteIcon />}
      text="Book Free Consultation"
      className="header__outline-button"
      onClick={() => navigate("/book-now")}
    />
  </>
));

// ConsultationButton component for reuse
const ConsultationButton = React.memo(({ navigate, onClose, className, showIcon = false }) => (
  <CustomButton
    text="Book Free Consultation"
    className={className}
    startIcon={showIcon ? <EventNoteIcon /> : undefined}
    onClick={() => {
      navigate("/book-now");
      if (onClose) onClose();
    }}
  />
));

function Header() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleOpenNavMenu = React.useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleCloseNavMenu = React.useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const navigateHome = React.useCallback(() => {
    navigate("/");
    handleCloseNavMenu();
  }, [navigate, handleCloseNavMenu]);

  const mobileDrawer = React.useMemo(
    () => (
      <Drawer
        anchor="left"
        variant="temporary"
        open={drawerOpen}
        onClose={handleCloseNavMenu}
        className="mobile-drawer"
      >
        {/* Logo/Header Section */}
        <CompanyLogo 
          className="mobile-drawer__box"
          iconClassName="mobile-drawer__AdbIcon"
          typographyClassName="mobile-drawer__typography"
          onClick={navigateHome}
        />

        <Divider sx={{ mx: 2 }} />

        {/* Navigation List */}
        <List sx={{ pt: 2, px: 1 }}>
          {navigationItems.map((item) => (
            <ListItem key={item.name} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.route);
                  handleCloseNavMenu();
                }}
                className="mobile-drawer__ListItemButton"
              >
                <ListItemIcon className="mobile-drawer__ListItemIcon">
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.name}
                  className="mobile-drawer__ListItemText"
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Consultation Button Section */}
        <Box className="consultation-button__box">
          <ConsultationButton 
            navigate={navigate}
            onClose={handleCloseNavMenu}
            className="consultation-button__button"
            showIcon={true}
          />
        </Box>
      </Drawer>
    ),
    [drawerOpen, handleCloseNavMenu, navigate, navigateHome]
  );

  const mainAppBar = React.useMemo(
    () => (
      <AppBar position="relative" className="main-appbar" height="112px">
        <Container maxWidth="xl" sx={{ height: "100%" }}>
          <Toolbar disableGutters className="main-appbar__toolbar" >
            <CompanyLogo 
              className="main-appbar__box"
              iconClassName="main-appbar__AdbIcon"
              typographyClassName="main-appbar__Typography"
              onClick={() => navigate("/")}
            />

            <Box sx={{ flexGrow: 1 }} />

            <Box className="main-appbar__navBox">
              <NavButtons navigate={navigate} />
            </Box>

            <Box className="main-appbar__IconBox">
              <IconButton
                size="large"
                aria-label="menu"
                onClick={handleOpenNavMenu}
                className="main-appbar__IconTypography"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    ),
    [handleOpenNavMenu, navigate]
  );

  return (
    <Box sx={{ position: "relative" }}>
      <TopHeader />
      {mainAppBar}
      {mobileDrawer}
    </Box>
  );
}

export default React.memo(Header);